"use client"

import { useState, useEffect } from "react"
import { Instagram, MessageCircle, Users, Heart, Sparkles } from "lucide-react"
import { FaLinkedin } from "react-icons/fa"
import drone from "../assets/drone.png" // adjust path as needed

/** Decorative floating symbol */
const FloatingElement = ({ type, index, delay = 0 }) => {
  const elements = {
    star: "★",
    circle: "●",
    diamond: "◆",
    triangle: "▲",
  }

  // Tailwind classes for each type/position:
  const posClass = {
    star1: "top-[15%] left-[20%] text-amber-400",
    star2: "top-[70%] right-[15%] text-pink-400",
    star3: "bottom-[20%] left-[10%] text-blue-400",
    circle1: "top-[30%] right-[30%] text-green-400",
    circle2: "bottom-[40%] right-[10%] text-purple-400",
    diamond1: "top-[50%] left-[5%] text-pink-500",
    triangle1: "top-[25%] left-[80%] text-cyan-400",
    triangle2: "bottom-[30%] right-[40%] text-amber-400",
  }
  const combined =
    {
      star1: posClass.star1,
      star2: posClass.star2,
      star3: posClass.star3,
      circle1: posClass.circle1,
      circle2: posClass.circle2,
      diamond1: posClass.diamond1,
      triangle1: posClass.triangle1,
      triangle2: posClass.triangle2,
    }[
    `${type}${index}` // e.g. 'star1'
    ] || ""

  return (
    <div
      className={`absolute text-[1.5rem] opacity-30 animate-floatElement ${combined} pointer-events-none`}
      style={{ animationDelay: `${delay}s` }}
    >
      {elements[type]}
    </div>
  )
}

const SocialButtonbtn = ({ icon: Icon, label, url, className, stats }) => {
  const [isHovered, setIsHovered] = useState(false)
  return (
    <button
      className={`flex items-center px-6 py-4 bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl font-semibold gap-4 text-white relative overflow-hidden cursor-pointer transition-all duration-500 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] ${className === "telegram"
        ? "border-[#0088cc]/30 hover:bg-[#0088cc]/10 hover:border-[#0088cc]/50 hover:shadow-[0_8px_32px_0_rgba(0,136,204,0.3)]"
        : className === "whatsapp"
          ? "border-[#25d366]/30 hover:bg-[#25d366]/10 hover:border-[#25d366]/50 hover:shadow-[0_8px_32px_0_rgba(37,211,102,0.3)]"
          : className === "instagram"
            ? "border-[#e4405f]/30 hover:bg-[#e4405f]/10 hover:border-[#e4405f]/50 hover:shadow-[0_8px_32px_0_rgba(228,64,95,0.3)]"
            : ""
        } hover:-translate-y-2 hover:shadow-2xl`}
      onClick={() => window.open(url, "_blank")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-4 relative z-10">
        <span className="flex items-center justify-center">
          <Icon size={24} />
        </span>
        <span className="flex flex-col items-start">
          <span className="text-lg">{label}</span>
          {stats && <span className="text-sm font-normal text-slate-200">{stats}</span>}
        </span>
      </div>
      <div
        className={`absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl pointer-events-none transition-all duration-500 ${isHovered ? "opacity-100" : "opacity-0"
          }`}
      />
      {/* Glass reflection effect */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/10 to-transparent rounded-t-2xl pointer-events-none" />
    </button>
  )
}

const CommunitySection = () => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true)
      },
      { threshold: 0.1 }
    )
    const el = document.querySelector(".community-section-root")
    if (el) observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const socialButtonsbtn = [
    {
      icon: FaLinkedin,
      label: "Join Linkedin",
      url: "https://www.linkedin.com/company/aeronauts-nsut",
      className: "telegram",
      stats: "500+ members",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp Group",
      url: "https://chat.whatsapp.com/E9UcYzGRrTxJne18VkE8zl",
      className: "whatsapp",
      stats: "Active daily",
    },
    {
      icon: Instagram,
      label: "Follow Us",
      url: "https://www.instagram.com/aeronauts.nsut",
      className: "instagram",
      stats: "Latest updates",
    },
  ]

  return (
    <div className="community-section-root relative min-h-screen bg-[#020617] py-20 px-4 md:px-8 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.1)_1px,transparent_0)] bg-[length:50px_50px] animate-gridMove" />
        <div className="absolute w-[300px] h-[300px] rounded-full blur-[60px] opacity-30 bg-[radial-gradient(circle,#3b82f6_0%,transparent_70%)] top-[10%] left-[10%] animate-orbFloat [animation-delay:0s]" />
        <div className="absolute w-[200px] h-[200px] rounded-full blur-[60px] opacity-30 bg-[radial-gradient(circle,#8b5cf6_0%,transparent_70%)] top-[60%] right-[20%] animate-orbFloat [animation-delay:2s]" />
        <div className="absolute w-[250px] h-[250px] rounded-full blur-[60px] opacity-30 bg-[radial-gradient(circle,#06b6d4_0%,transparent_70%)] bottom-[20%] left-[30%] animate-orbFloat [animation-delay:4s]" />
      </div>
      {/* Floating Decorative Elements */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <FloatingElement type="star" index={1} delay={0} />
        <FloatingElement type="star" index={2} delay={0.5} />
        <FloatingElement type="star" index={3} delay={1} />
        <FloatingElement type="circle" index={1} delay={0.2} />
        <FloatingElement type="circle" index={2} delay={0.7} />
        <FloatingElement type="diamond" index={1} delay={0.3} />
        <FloatingElement type="triangle" index={1} delay={0.8} />
        <FloatingElement type="triangle" index={2} delay={1.2} />
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto grid md:grid-cols-2 grid-cols-1 gap-16 min-h-[80vh] items-center">
        {/* Left Visual Section */}
        <div
          className={`flex flex-col items-center gap-12 transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}
        >
          {/* Drone + Animations */}
          {/* Drone + Animations */}
          <div className="relative 
  w-[220px] h-[220px]         // mobile
  sm:w-[400px] sm:h-[340px]  // small
  md:w-[500px] md:h-[500px]  // medium
  lg:w-[600px] lg:h-[500px]  // large
  xl:w-[700px] xl:h-[500px]  // extra large
  flex items-center justify-center">
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={drone}
                loading="lazy"
                alt="Aeronauts Drone"
                className="w-full h-auto drop-shadow-xl transition-transform duration-300 hover:scale-110 animate-droneFly"
              />
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[120px] h-[120px] sm:w-[200px] sm:h-[200px] md:w-[280px] md:h-[280px] 
        lg:w-[400px] lg:h-[400px] xl:w-[600px] xl:h-[600px] 2xl:w-[800px] 2xl:h-[800px]
        bg-[radial-gradient(circle,rgba(59,130,246,0.2)_0%,transparent_70%)] rounded-full animate-pulseGlow z-[-1]"
              />
            </div>
            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-gradient-to-tr from-blue-400 to-purple-500 rounded-full animate-particleFloat"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                />
              ))}
            </div>
          </div>


          {/* Community Stats */}
          <div className="flex gap-8 md:w-[350px] flex-wrap justify-center">
            <div className="flex items-center gap-2 px-4 py-4 bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] min-w-[120px] relative overflow-hidden">
              <Users className="w-5 h-5 text-blue-400" />
              <div className="flex flex-col">
                <span className="font-bold text-lg text-white">500+</span>
                <span className="text-xs text-slate-300">Members</span>
              </div>
              <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/10 to-transparent rounded-t-2xl pointer-events-none" />
            </div>
            <div className="flex items-center gap-2 px-4 py-4 bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] min-w-[120px] relative overflow-hidden">
              <Heart className="w-5 h-5 text-blue-400" />
              <div className="flex flex-col">
                <span className="font-bold text-lg text-white">24/7</span>
                <span className="text-xs text-slate-300">Active</span>
              </div>
              <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/10 to-transparent rounded-t-2xl pointer-events-none" />
            </div>
            <div className="flex items-center gap-2 px-4 py-4 bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] min-w-[120px] relative overflow-hidden">
              <Sparkles className="w-5 h-5 text-blue-400" />
              <div className="flex flex-col">
                <span className="font-bold text-lg text-white">Daily</span>
                <span className="text-xs text-slate-300">Updates</span>
              </div>
              <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/10 to-transparent rounded-t-2xl pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Right Content Section */}
        <div
          className={`flex flex-col gap-8 transition-all duration-1000 delay-300
    ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}
    items-center md:items-start text-center md:text-left`}
        >
          {/* Header */}
          <div className="mb-4 w-full flex justify-center md:justify-start">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-200 border border-blue-300/30 rounded-full text-[0.9rem] font-medium">
              <Users size={16} />
              <span>Join Our Community</span>
            </div>
          </div>
          {/* Animated Title */}
          <h2 className="font-bold text-[2.1rem] xs:text-[2.5rem] md:text-[3rem] leading-tight mt-0 mb-2 w-full">
            <div className="block bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent animate-titleSlideIn">We speak,</div>
            <div className="block bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent animate-titleSlideIn">we listen, we</div>
            <div className="block bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent animate-titleSlideIn">discuss, we</div>
            <div className="block bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent animate-titleSlideIn">grow.</div>
          </h2>
          <p className="text-slate-300 text-lg mb-4 w-full">
            Share ideas, feedback, and connect with passionate individuals who share your love for drone technology and innovation. Join our thriving community today!
          </p>
          {/* Social Buttons */}
          <div className="flex flex-col gap-4 w-full md:w-[400px]">
            {socialButtonsbtn.map((button, idx) => (
              <SocialButtonbtn
                key={idx}
                icon={button.icon}
                label={button.label}
                url={button.url}
                className={button.className}
                stats={button.stats}
              />
            ))}
          </div>
          {/* Call to Action */}
          <a
            href="https://www.instagram.com/aeronauts.nsut/#"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div className="flex items-center gap-4 mt-8 px-6 py-4 bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl cursor-pointer transition-all duration-500 hover:scale-[1.03] hover:-translate-y-1 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] relative overflow-hidden">
              <span className="text-slate-200 font-medium relative z-10">
                Ready to be part of something amazing?
              </span>
              <span className="text-blue-400 text-2xl animate-arrowBounce relative z-10">→</span>
              <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/10 to-transparent rounded-t-2xl pointer-events-none" />
            </div>
          </a>

        </div>
      </div>
    </div>
  )
}

export default CommunitySection
